/**
 * Pain Trend Utilities
 *
 * Pure helper functions for processing pain data for trend charts.
 * Handles data transformation, date formatting, and chart data preparation.
 *
 * Where possible, the heavy-lifting is delegated to @floline/journal-aggregation
 * so that the stats / location-frequency / daily-series formulas stay in sync
 * with server-side exports and AI grounding context.  UI-specific transforms
 * (chart axis labels, event markers/spans, medication spans) remain local.
 */

import type {
  DailyRecordResponse,
  BodyZone,
  BodySide,
  BodySurface,
  BodyRegion,
  Laterality,
  EventResponse,
  EventType,
  UserMedicationResponse,
} from "@floline/api-client";
import type { TimelineEventData } from "../timeline/timeline-event-utils";
import { isOngoingMedicationType } from "../timeline/timeline-event-utils";
import {
  mean,
  roundTo,
  computePainStatsFromValues,
  computeLocationFrequencies,
  computeDailyPainSeries,
  formatLocalDateYmd,
  type LocationFrequency as SharedLocationFrequency,
  type DailyPainPoint,
} from "@floline/journal-aggregation";
import { formatDateYmdMonthDayDisplay } from "@/lib/date-format";

// ============================================================================
// Types
// ============================================================================

/**
 * Data point for pain trend chart
 */
export interface TrendDataPoint {
  /** Date in YYYY-MM-DD format */
  date: string;
  /** Display date for x-axis (e.g., "Jan 5") */
  displayDate: string;
  /** Average pain intensity (0-10) or null if no data */
  averageIntensity: number | null;
  /** Number of pain locations recorded */
  locationCount: number;
  /** Maximum pain intensity or null if no data */
  maxIntensity: number | null;
  /** Minimum pain intensity or null if no data */
  minIntensity: number | null;
  /** Whether this date has any data */
  hasData: boolean;
}

/**
 * Event marker for chart overlay
 */
export interface ChartEventMarker {
  /** Date in YYYY-MM-DD format */
  date: string;
  /** Events on this date */
  events: TimelineEventData[];
  /** Y position for marker (typically at top of chart) */
  y: number;
}

/**
 * Event span for chart background overlays
 */
export interface ChartEventSpan {
  /** Event ID */
  id: string;
  /** Event ID to open in dialogs (may be null for medication-only spans) */
  eventId: string | null;
  /** Event type */
  eventType: EventType;
  /** Event title */
  title: string;
  /** Start date clipped to visible range */
  startDate: string;
  /** End date clipped to visible range */
  endDate: string;
  /** Parent event ID for dose changes (links back to original medication_start) */
  parentEventId: string | null;
  /** Whether this span represents an ongoing medication (no explicit end date) */
  isOngoing: boolean;
  /** Original (unclipped) start date — used to determine if the span truly starts within a selected range */
  originalStartDate: string;
  /** Original (unclipped) end date — used to determine if the span truly ends within a selected range */
  originalEndDate: string;
}

/**
 * Date range options for trends
 */
export type DateRangeOption = "7d" | "30d" | "90d" | "180d" | "365d" | "all";

// ============================================================================
// Date Utilities
// ============================================================================

/**
 * Get the start date for a date range option
 *
 * @param option - Date range selection
 * @param referenceDate - Reference date (defaults to today)
 * @returns Start date in YYYY-MM-DD format
 */
export function getDateRangeStart(
  option: DateRangeOption,
  referenceDate = new Date()
): string {
  if (option === "all") {
    // Return a date far in the past
    return "2000-01-01";
  }
  
  const days = {
    "7d": 7,
    "30d": 30,
    "90d": 90,
    "180d": 180,
    "365d": 365,
  }[option];
  
  const start = new Date(referenceDate);
  start.setDate(start.getDate() - days + 1);
  
  return formatLocalDate(start);
}

/**
 * Format a date as YYYY-MM-DD in local timezone
 *
 * Delegates to the shared `formatLocalDateYmd` helper which uses
 * local getters (not UTC), avoiding timezone-shift bugs.
 *
 * @param date - Date to format
 * @returns Formatted date string
 */
export function formatLocalDate(date: Date): string {
  return formatLocalDateYmd(date);
}

/**
 * Format a date for display on chart axis (e.g., "Jan 5")
 *
 * @param dateString - Date in YYYY-MM-DD format
 * @returns Formatted display string
 */
export function formatAxisDate(dateString: string, locale = "en-US"): string {
  const language = locale.toLowerCase().startsWith("fi") ? "fi" : "en";
  return formatDateYmdMonthDayDisplay(dateString, { language });
}

/**
 * Generate array of dates between start and end (inclusive)
 *
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @returns Array of date strings
 */
export function generateDateRange(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  const current = new Date(`${startDate}T12:00:00`);
  const end = new Date(`${endDate}T12:00:00`);
  
  while (current <= end) {
    dates.push(formatLocalDate(current));
    current.setDate(current.getDate() + 1);
  }
  
  return dates;
}

// ============================================================================
// Data Transformation
// ============================================================================

/**
 * Calculate average pain intensity from pain locations
 *
 * Delegates to the shared `mean` + `roundTo` helpers.
 *
 * @param painLocations - Array of pain location entries
 * @returns Average intensity rounded to 1 decimal, or null if no locations
 */
export function calculateAverageIntensity(
  painLocations: Array<{ intensityScore: number }>
): number | null {
  if (painLocations.length === 0) return null;
  const values = painLocations.map((loc) => loc.intensityScore);
  return roundTo(mean(values), 1);
}

/**
 * Calculate min/max pain intensity from pain locations
 *
 * @param painLocations - Array of pain location entries
 * @returns Object with min and max, or nulls if no locations
 */
export function calculateMinMaxIntensity(
  painLocations: Array<{ intensityScore: number }>
): { min: number | null; max: number | null } {
  if (painLocations.length === 0) {
    return { min: null, max: null };
  }
  
  const scores = painLocations.map((loc) => loc.intensityScore);
  return {
    min: Math.min(...scores),
    max: Math.max(...scores),
  };
}

/**
 * Convert daily records to a map keyed by date
 *
 * @param records - Array of daily record responses
 * @returns Map from date string to record
 */
export function recordsByDate(
  records: DailyRecordResponse[]
): Map<string, DailyRecordResponse> {
  const map = new Map<string, DailyRecordResponse>();
  for (const record of records) {
    map.set(record.recordDate, record);
  }
  return map;
}

/**
 * Build trend data points for a date range
 *
 * Creates a data point for each day in the range, with nulls for days
 * without records.  The core per-record aggregation is delegated to
 * `computeDailyPainSeries` (with `treatEmptyAsZero: true`) so that
 * a check-in with no pain locations displays as "pain-free = 0".
 *
 * UI-specific additions (displayDate, min/max per day) are computed
 * locally because the shared series intentionally omits chart concerns.
 *
 * @param records - Array of daily records
 * @param startDate - Start of date range
 * @param endDate - End of date range
 * @returns Array of trend data points
 */
export function buildTrendDataPoints(
  records: DailyRecordResponse[],
  startDate: string,
  endDate: string
): TrendDataPoint[] {
  // Use the shared daily series to compute per-date avgPain & locationCount.
  const dailySeries = computeDailyPainSeries(records, { treatEmptyAsZero: true });
  const seriesMap = new Map<string, DailyPainPoint>();
  for (const pt of dailySeries) {
    seriesMap.set(pt.date, pt);
  }

  // Build a record map for min/max (shared series doesn't carry those).
  const recordMap = recordsByDate(records);
  const dates = generateDateRange(startDate, endDate);
  
  return dates.map((date) => {
    const seriesPoint = seriesMap.get(date);
    
    if (!seriesPoint) {
      // No record for this date — gap day.
      return {
        date,
        displayDate: formatAxisDate(date),
        averageIntensity: null,
        locationCount: 0,
        maxIntensity: null,
        minIntensity: null,
        hasData: false,
      };
    }

    // We have a record.  Min/max come from the raw painLocations.
    const record = recordMap.get(date);
    const painLocations = record?.painLocations ?? [];

    if (painLocations.length === 0) {
      // Pain-free record: treatEmptyAsZero means avgPain === 0.
      return {
        date,
        displayDate: formatAxisDate(date),
        averageIntensity: seriesPoint.avgPain,
        locationCount: 0,
        maxIntensity: 0,
        minIntensity: 0,
        hasData: true,
      };
    }

    const { min, max } = calculateMinMaxIntensity(painLocations);
    
    return {
      date,
      displayDate: formatAxisDate(date),
      averageIntensity: seriesPoint.avgPain,
      locationCount: seriesPoint.locationCount,
      maxIntensity: max,
      minIntensity: min,
      hasData: true,
    };
  });
}

// ============================================================================
// Event Marker Utilities
// ============================================================================

/**
 * Build chart event markers from events grouped by date
 *
 * @param eventsByDate - Events grouped by date
 * @param dateRange - Array of dates in the current view
 * @param maxY - Maximum Y value for marker placement (default: 10)
 * @returns Array of chart event markers
 */
export function buildEventMarkers(
  eventsByDate: Record<string, TimelineEventData[]>,
  dateRange: string[],
  maxY = 10
): ChartEventMarker[] {
  const markers: ChartEventMarker[] = [];
  
  for (const date of dateRange) {
    const events = eventsByDate[date];
    if (events && events.length > 0) {
      markers.push({
        date,
        events,
        y: maxY,
      });
    }
  }
  
  return markers;
}

/**
 * Build event spans for chart background overlays.
 */
export function buildEventSpans(
  events: EventResponse[],
  startDate: string,
  endDate: string
): ChartEventSpan[] {
  const spans: ChartEventSpan[] = [];
  const today = formatLocalDate(new Date());

  for (const event of events) {
    // For ongoing medication events (no endDate), use today as effective end
    const isOngoing = event.endDate === null && isOngoingMedicationType(event.eventType);
    const rawEnd = isOngoing ? today : (event.endDate ?? event.startDate);
    const overlapsRange = event.startDate <= endDate && rawEnd >= startDate;

    if (!overlapsRange) {
      continue;
    }

    spans.push({
      id: event.id,
      eventId: event.id,
      eventType: event.eventType,
      title: event.title,
      startDate: event.startDate < startDate ? startDate : event.startDate,
      endDate: rawEnd > endDate ? endDate : rawEnd,
      parentEventId: event.parentEventId ?? null,
      isOngoing,
      originalStartDate: event.startDate,
      originalEndDate: rawEnd,
    });
  }

  return spans.sort((a, b) => {
    const startCompare = a.startDate.localeCompare(b.startDate);
    if (startCompare !== 0) {
      return startCompare;
    }
    return a.endDate.localeCompare(b.endDate);
  });
}

/**
 * Build medication duration spans from `user_medications` rows.
 *
 * Returns a span for **every** medication that overlaps the visible range,
 * regardless of `showInTrends`. The `showInTrends` flag is a chart-overlay
 * concern handled downstream (see `filterChartOnlySpans`).
 *
 * Ongoing medications (no endDate) extend to today. Spans are clipped to
 * the visible chart date range.
 *
 * @param medications - Array of user medication responses
 * @param startDate - Start of visible chart range (YYYY-MM-DD)
 * @param endDate - End of visible chart range (YYYY-MM-DD)
 * @returns Array of chart event spans for medication durations, sorted by start date
 */
export function buildMedicationSpans(
  medications: UserMedicationResponse[],
  startDate: string,
  endDate: string,
  events: EventResponse[] = [],
): ChartEventSpan[] {
  const spans: ChartEventSpan[] = [];
  const today = formatLocalDate(new Date());

  // Build a best-effort mapping from user_medications.id -> events.id so
  // medication spans can open the corresponding Event detail dialog.
  // Note: seeded medications may exist without any linked events.
  const medIdToEventId = new Map<string, { id: string; startDate: string; priority: number }>();
  for (const e of events) {
    if (!e.relatedMedicationId) continue;

    // Prefer the event that created the medication record when available.
    // Fall back to any linked event.
    const priority = e.eventType === "medication_start" || e.eventType === "medication_switch" ? 2 : 1;
    const existing = medIdToEventId.get(e.relatedMedicationId);
    if (!existing) {
      medIdToEventId.set(e.relatedMedicationId, { id: e.id, startDate: e.startDate, priority });
      continue;
    }

    if (priority > existing.priority) {
      medIdToEventId.set(e.relatedMedicationId, { id: e.id, startDate: e.startDate, priority });
      continue;
    }

    // If the priority matches, prefer the earliest event as the "root".
    if (priority === existing.priority && e.startDate < existing.startDate) {
      medIdToEventId.set(e.relatedMedicationId, { id: e.id, startDate: e.startDate, priority });
    }
  }

  for (const med of medications) {
    const isOngoing = med.endDate === null;
    const rawEnd = isOngoing ? today : (med.endDate as string);

    // Check overlap with visible range
    const overlapsRange = med.startDate <= endDate && rawEnd >= startDate;
    if (!overlapsRange) continue;

    spans.push({
      id: med.id,
      eventId: medIdToEventId.get(med.id)?.id ?? null,
      eventType: "medication_start" as EventType,
      title: med.displayName,
      startDate: med.startDate < startDate ? startDate : med.startDate,
      endDate: rawEnd > endDate ? endDate : rawEnd,
      parentEventId: null,
      isOngoing,
      originalStartDate: med.startDate,
      originalEndDate: rawEnd,
    });
  }

  return spans.sort((a, b) => {
    const startCompare = a.startDate.localeCompare(b.startDate);
    if (startCompare !== 0) return startCompare;
    return a.endDate.localeCompare(b.endDate);
  });
}

/**
 * Filter medication spans to only those the user wants on the chart.
 *
 * Accepts the full set of medication IDs whose `showInTrends` flag is true
 * and returns only the event spans whose `id` is in that set.
 * Non-medication spans (different `eventType`) are always kept.
 *
 * @param spans - All event spans (medications + other events)
 * @param chartVisibleMedIds - Set of medication IDs with showInTrends === true
 * @returns Filtered spans suitable for chart overlays
 */
export function filterChartOnlySpans(
  spans: ChartEventSpan[],
  chartVisibleMedIds: Set<string>,
): ChartEventSpan[] {
  return spans.filter(
    (span) =>
      span.eventType !== ("medication_start" as EventType) ||
      chartVisibleMedIds.has(span.id),
  );
}

/**
 * Filter events by date range
 *
 * @param eventsByDate - Events grouped by date
 * @param startDate - Start of range (inclusive)
 * @param endDate - End of range (inclusive)
 * @returns Filtered events by date
 */
export function filterEventsByDateRange(
  eventsByDate: Record<string, TimelineEventData[]>,
  startDate: string,
  endDate: string
): Record<string, TimelineEventData[]> {
  const result: Record<string, TimelineEventData[]> = {};
  
  for (const [date, events] of Object.entries(eventsByDate)) {
    if (date >= startDate && date <= endDate) {
      result[date] = events;
    }
  }
  
  return result;
}

// ============================================================================
// Statistics
// ============================================================================

/**
 * Calculate summary statistics for trend data
 *
 * Delegates the core mean/min/max computation to `computePainStatsFromValues`
 * from the shared aggregation package.
 *
 * @param dataPoints - Array of trend data points
 * @returns Statistics object
 */
export function calculateTrendStatistics(dataPoints: TrendDataPoint[]): {
  daysWithData: number;
  totalDays: number;
  averageIntensity: number | null;
  maxIntensity: number | null;
  minIntensity: number | null;
} {
  const withData = dataPoints.filter((dp) => dp.hasData);
  const totalDays = dataPoints.length;
  const daysWithData = withData.length;
  
  if (daysWithData === 0) {
    return {
      daysWithData,
      totalDays,
      averageIntensity: null,
      maxIntensity: null,
      minIntensity: null,
    };
  }
  
  // Use shared stats for the average-of-averages (one value per day).
  const avgValues = withData
    .map((dp) => dp.averageIntensity)
    .filter((v): v is number => v !== null);
  
  const stats = computePainStatsFromValues(avgValues);

  // Overall max/min come from the per-day maxIntensity/minIntensity, not
  // from the averages, so we still compute those locally.
  const maxValues = withData
    .map((dp) => dp.maxIntensity)
    .filter((v): v is number => v !== null);
  
  const minValues = withData
    .map((dp) => dp.minIntensity)
    .filter((v): v is number => v !== null);
  
  const overallMax = maxValues.length > 0 ? Math.max(...maxValues) : null;
  const overallMin = minValues.length > 0 ? Math.min(...minValues) : null;
  
  return {
    daysWithData,
    totalDays,
    averageIntensity: stats.mean,
    maxIntensity: overallMax,
    minIntensity: overallMin,
  };
}

/**
 * Get display label for date range option
 *
 * @param option - Date range option
 * @returns Human-readable label
 */
export function getDateRangeLabel(option: DateRangeOption): string {
  const labels: Record<DateRangeOption, string> = {
    "7d": "7 Days",
    "30d": "30 Days",
    "90d": "90 Days",
    "180d": "6 Months",
    "365d": "1 Year",
    "all": "All Time",
  };
  return labels[option];
}

// ============================================================================
// Location Analysis
// ============================================================================

/**
 * Location frequency data
 *
 * This is the UI-facing shape that the Trends components expect.
 * The shared package produces a slightly different shape
 * (`daysReported`, `percentOfDays`, `avgIntensity`), so we map
 * the fields in `calculateLocationFrequency` below.
 */
export interface LocationFrequency {
  zone?: BodyZone;
  side?: BodySide;
  surface?: BodySurface;
  region: BodyRegion;
  laterality: Laterality;
  count: number;
  averageIntensity: number;
  percentage: number;
}

export type LocationRollupMode =
  | "zone_side_surface"
  | "zone_side"
  | "zone";

/**
 * Calculate location frequency from records
 *
 * Delegates to the shared `computeLocationFrequencies` and maps
 * the result to the UI-specific `LocationFrequency` shape.
 *
 * @param records - Array of daily records
 * @returns Array of location frequencies sorted by count
 */
export function calculateLocationFrequency(
  records: DailyRecordResponse[]
): LocationFrequency[] {
  const shared = computeLocationFrequencies(records, records.length);

  const frequencies: LocationFrequency[] = shared.map((sf: SharedLocationFrequency) => ({
    zone: sf.zone as BodyZone | undefined,
    side: sf.side as BodySide | undefined,
    surface: sf.surface as BodySurface | undefined,
    region: sf.region as BodyRegion,
    laterality: sf.laterality as Laterality,
    count: sf.daysReported,
    averageIntensity: sf.avgIntensity,
    percentage: records.length > 0
      ? Math.round((sf.daysReported / records.length) * 100)
      : 0,
  }));

  return frequencies.sort((a, b) => b.count - a.count);
}

/**
 * Roll up location frequencies for trends display.
 * Default detailed mode keeps zone+side+surface distinct.
 */
export function rollupLocationFrequencies(
  frequencies: LocationFrequency[],
  mode: LocationRollupMode
): LocationFrequency[] {
  if (mode === "zone_side_surface") return frequencies;

  const grouped = new Map<string, LocationFrequency>();

  for (const freq of frequencies) {
    const key =
      mode === "zone"
        ? `${freq.zone ?? freq.region}`
        : `${freq.zone ?? freq.region}:${freq.side ?? freq.laterality}`;

    const current = grouped.get(key);
    if (!current) {
      grouped.set(key, {
        ...freq,
        surface: mode === "zone_side" ? undefined : freq.surface,
        laterality: mode === "zone" ? "unknown" : freq.laterality,
      });
      continue;
    }

    const mergedCount = current.count + freq.count;
    const weightedIntensity =
      mergedCount > 0
        ? (current.averageIntensity * current.count + freq.averageIntensity * freq.count) /
          mergedCount
        : 0;

    grouped.set(key, {
      ...current,
      count: mergedCount,
      averageIntensity: Math.round(weightedIntensity * 10) / 10,
      percentage: Math.round((current.percentage + freq.percentage) * 10) / 10,
      surface: mode === "zone_side" ? undefined : current.surface,
      laterality: mode === "zone" ? "unknown" : current.laterality,
    });
  }

  return [...grouped.values()].sort((a, b) => b.count - a.count);
}
